import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom'; // Ajout de Navigate pour redirection
import { FiSlash } from 'react-icons/fi';

import Auth from './components/Auth';
import PatientsList from './components/patients/PatientsList';
import PatientRecord from './components/patients/PatientRecord';
import PatientForm from './components/patients/PatientForm';
import { PatientDoctorProvider } from './components/PatientDoctorContext';
import ArchivesPage from './components/archives/ArchivesPage';
import PaymentsPage from './components/payments/PaymentsPage';
import CroList from './components/cro/CroList';
import CroDetail from './components/cro/CroDetail';
import CroForm from './components/cro/CroForm';

import Home from './components/Home';
import Role from './components/Role';
import 'bootstrap/dist/css/bootstrap.min.css';
import UserManagement from './components/UserManagement';

import EchoList from './components/echo/EchoList';
import EchoDetail from './components/echo/EchoDetail';
import EchoForm from './components/echo/EchoForm';

import ProgrammePage from './components/programme/ProgrammePage';

import StaffPage from './components/staff/StaffPage';

import MessagesPage from './components/messages/MessagesPage';
import IconographiePage from './components/iconographie/IconographiePage';
import GardesPage from './components/gardes/GardesPage';
import { getSessionUser, hasValidSession } from './lib/session';
import { canSee } from './lib/permissions';
import AreaGate from './components/ui/AreaGate';
import AppShell, { HOME_PATH } from './components/ui/AppShell';

// Anciennes adresses : les pages remplacées mènent à leur nouvelle version.
const RETIRED_PAGES = {
  '/patients/ancienne-version': '/patients',
  '/docaccess': '/patients',
  '/doctors': '/patients',
  '/patientfiles': '/patients',
  '/copiefiles': '/patients',
  '/filepatient': '/patients',
  '/cpdropboxform': '/archive',
  '/archivelist': '/archive',
  '/connections': '/usermanagement',
  '/loginHistory': '/usermanagement',
  '/user-management': '/usermanagement',
  '/cp': '/role',
  '/mode': '/role',
  '/userconnection': '/role',
};

// Anciennes pages de connexion par rôle : le rôle vient désormais de la connexion unique.
const RETIRED_LOGIN_PATHS = [
  '/AuthenticationGestionnaire',
  '/AuthenticationEtudiant',
  '/AuthenticationInfirmiers',
  '/AuthenticationArchiviste',
  '/AuthenticationMedecin',
  '/AuthenticationSecretaire',
  '/authentifications',
  '/authentification',
];

// Route protégée : exige une session valide (vérifiée à nouveau par le serveur à chaque requête)
const ProtectedRoute = ({ element }) => (hasValidSession() ? element : <Navigate to="/auth" replace />);

const AdminRoute = ({ element }) => {
  if (!hasValidSession()) return <Navigate to="/auth" replace />;
  const user = getSessionUser();
  return user && user.role === 'Admin' ? element : <Navigate to="/role" replace />;
};

const SectionClosed = () => (
  <AppShell>
    <main className="mx-auto flex max-w-xl flex-col items-center px-[20px] py-24 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
        <FiSlash size={22} aria-hidden="true" />
      </span>
      <h1 className="mb-0 mt-[20px] text-[24px] font-semibold tracking-tight text-ink">
        Section non ouverte pour votre compte
      </h1>
      <p className="mb-0 mt-2 text-[15px] leading-relaxed text-black/55">
        Si vous en avez besoin, demandez à l’administrateur de modifier vos droits.
      </p>
      <Link
        to={HOME_PATH}
        className="mt-6 inline-flex h-11 items-center rounded-full bg-brand-600 px-[20px] text-[14px] font-medium text-white no-underline transition hover:bg-brand-700 hover:text-white"
      >
        Retour au tableau de bord
      </Link>
    </main>
  </AppShell>
);

// Section de la plateforme : session valide + droit sur la section (grille des droits du
// serveur), affichée dans le cadre commun (menu latéral) sauf pour les pages qui ont le leur.
const SectionRoute = ({ section, element, framed = true }) => {
  if (!hasValidSession()) return <Navigate to="/auth" replace />;
  if (!canSee(section)) return <SectionClosed />;
  return framed ? <AppShell>{element}</AppShell> : element;
};

// Espace sensible : droit sur la section + code d'accès personnel défini par l'administrateur.
const SensitiveRoute = ({ area, title, element }) => (
  <SectionRoute section={area} element={<AreaGate area={area} title={title}>{element}</AreaGate>} />
);

const App = () => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // The connections are recorded by the server (Administration > journal).
  const login = (token) => {
    setToken(token);
    localStorage.setItem('token', token); // Enregistrer le token dans le localStorage
    localStorage.removeItem('connectionHistory');
  };

  return (
    <PatientDoctorProvider> {/* Fournisseur de contexte des patients et médecins */}
      <Router>
        <div>
          <Routes>
            {/* Route publique */}
            <Route path="/auth" element={<Auth login={login} />} />
            <Route path="/" element={<Home token={token} />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/passwordauth" element={<Navigate to="/patients" replace />} />

            {/* Routes protégées */}
            <Route path="/iconographie" element={<SectionRoute section="iconographie" element={<IconographiePage />} />} />
            <Route path="/garde" element={<SectionRoute section="gardes" element={<GardesPage />} />} />

            <Route path="/message" element={<SectionRoute section="messages" element={<MessagesPage />} />} />
            <Route path="/echo" element={<SectionRoute section="echo" element={<EchoList />} />} />
            <Route path="/echo/nouvelle" element={<SectionRoute section="echo" element={<EchoForm />} />} />
            <Route path="/echo/:id" element={<SectionRoute section="echo" element={<EchoDetail />} />} />
            <Route path="/echo/:id/modifier" element={<SectionRoute section="echo" element={<EchoForm />} />} />
            <Route path="/patients" element={<SectionRoute section="patients" element={<PatientsList />} />} />
            <Route path="/patients/nouveau" element={<SectionRoute section="patients" element={<PatientForm />} />} />
            <Route path="/patients/:id" element={<SectionRoute section="patients" element={<PatientRecord />} />} />
            <Route path="/patients/:id/modifier" element={<SectionRoute section="patients" element={<PatientForm />} />} />
            <Route path="/archive" element={<SensitiveRoute area="archives" title="Archives" element={<ArchivesPage />} />} />
            <Route path="/archiveaccess" element={<Navigate to="/archive" replace />} />
            <Route path="/patientsolvable" element={<SensitiveRoute area="payments" title="Paiements" element={<PaymentsPage />} />} />
            <Route path="/fichiers" element={<Navigate to="/patients" replace />} />
            <Route path="/cro" element={<SectionRoute section="cro" element={<CroList />} />} />
            <Route path="/cro/nouveau" element={<SectionRoute section="cro" element={<CroForm />} />} />
            <Route path="/cro/:id" element={<SectionRoute section="cro" element={<CroDetail />} />} />
            <Route path="/cro/:id/modifier" element={<SectionRoute section="cro" element={<CroForm />} />} />
            <Route path="/fichierscro" element={<Navigate to="/cro" replace />} />

            <Route path="/solvabilite" element={<Navigate to="/patientsolvable" replace />} />
            <Route path="/role" element={<ProtectedRoute element={<Role token={token} />} />} />
            <Route path="/accesssecretaire" element={<Navigate to="/patients" replace />} />

            {/* Autres routes */}
            <Route path="/usermanagement" element={<AdminRoute element={<UserManagement token={token} />} />} />
            <Route path="/PwdAdmin" element={<Navigate to="/usermanagement" replace />} />
            {RETIRED_LOGIN_PATHS.map((path) => (
              <Route key={path} path={path} element={<Navigate to="/role" replace />} />
            ))}
            {Object.entries(RETIRED_PAGES).map(([path, target]) => (
              <Route key={path} path={path} element={<Navigate to={target} replace />} />
            ))}

            <Route path="/programme" element={<SectionRoute section="programme" element={<ProgrammePage />} />} />
            <Route path="/programmeAll" element={<Navigate to="/programme" replace />} />
            <Route path="/staff" element={<SectionRoute section="staff" element={<StaffPage />} />} />
            <Route path="/Observation" element={<Navigate to="/staff" replace />} />
            <Route path="/observationcuomo" element={<Navigate to="/staff" replace />} />

          </Routes>
        </div>
      </Router>
    </PatientDoctorProvider>
  );
};

export default App;
